import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { ProcedureType } from '@/types/cgt'

// ─── Resource capability matrix (Configure → Method) ────────────────────────
//
// Who can perform which procedures, and who is qualified in which methods.
// Master data: nothing reads it to gate assignment yet — filtering the
// process-step assignee picker by it is a later ticket.
//
// "Resource" is a label, not an entity. This app has no Resource table; the
// roster of people is User, and a row binds to a user id. The UI says
// "Resource Name" to match the mockup.
//
// Every route is tenant-scoped, so every call carries the tenant header — the
// matrix is one tenant's answer, and switching tenant must never show another's.

/** One person offered as a resource. */
export interface ResourceOption {
  id: string
  name: string
  email: string
}

/** One procedure offered: a template FAMILY, named by its current version. */
export interface ProcedureOption {
  familyId: string
  name: string
  version: number
  /**
   * Transplant, KIT Creation, MES, Cleaning, CAPA. The picker offers every type
   * — a resource is qualified to run kit builds and manufacturing orders too —
   * so the grid names the kind of work each row is for. Optional so an older
   * backend reads as "unknown" rather than mislabelling every row.
   */
  type?: ProcedureType | null
  /** May perform the method's steps (z8nrz7f0vv). */
  canProcess?: boolean
  /** May review the method's steps. */
  canReview?: boolean
  /** Last day it holds, YYYY-MM-DD; null = no expiry. */
  qualifiedUntil?: string | null
  /** Past qualifiedUntil: kept as history, licenses nothing. */
  expired?: boolean
}

/** Everything the pickers offer, in one request. */
export interface CapabilityOptions {
  resources: ResourceOption[]
  procedures: ProcedureOption[]
  /** The tenant's methods — rows now, not the labels of an option set. */
  methods: MethodOption[]
  /** Every Process field a method can be built from. */
  processSteps: ProcessStepOption[]
}

/** One method offered by the qualification picker. */
export interface MethodOption {
  id: string
  name: string
  /** A method covering no steps qualifies nobody for anything, so it is shown. */
  stepCount: number
}

/** One process step offered by a method's step picker. */
export interface ProcessStepOption {
  /** The Process FIELD id — what a method's membership stores. */
  fieldId: string
  label: string
  formId: string
  formName: string
  formVersion: number
}

/**
 * A METHOD: a named collection of process steps.
 *
 * "In each procedure we can have multiple processes — it's about which processes
 * one is capable to perform. A method has processes, and a person can be given a
 * method they're capable of." So the method is the unit of qualification, and
 * its steps are what being qualified in it licenses.
 */
export interface MethodRow {
  id: string
  name: string
  description: string | null
  steps: MethodStepRow[]
  /** How many people hold it — what deleting it would revoke. */
  resourceCount: number
}

export interface MethodStepRow {
  fieldId: string
  /** Null when no active form embeds this field any more: stale, not broken. */
  label: string | null
  formName: string | null
}

export interface ResourceProcedureRow {
  id: string
  userId: string
  resourceName: string
  procedureFamilyId: string
  /** Null when the template has been retired — the row is stale, not broken. */
  procedureName: string | null
  /** Null for the same reason `procedureName` is: nothing active to read it from. */
  procedureType?: ProcedureType | null
}

export interface ResourceMethodRow {
  id: string
  userId: string
  resourceName: string
  methodId: string
  /** Resolved server-side, so the grid renders a name without joining. */
  methodName: string
  /** Rank among this person's methods — LOWER IS PREFERRED. */
  preference: number
  /** Percentage, 0–100. */
  proficiency: number
  /**
   * Derived server-side from the most recent completed execution of any process
   * step THIS METHOD COVERS. Null when they have performed none — which is also
   * the case for a method that covers no steps yet.
   */
  lastUsedAt: string | null
  /** May perform the method's steps (z8nrz7f0vv). */
  canProcess?: boolean
  /** May review the method's steps. */
  canReview?: boolean
  /** Last day it holds, YYYY-MM-DD; null = no expiry. */
  qualifiedUntil?: string | null
  /** Past qualifiedUntil: kept as history, licenses nothing. */
  expired?: boolean
}

export interface CreateResourceProcedurePayload {
  userId: string
  procedureFamilyId: string
}

export type UpdateResourceProcedurePayload = Partial<CreateResourceProcedurePayload>

export interface CreateResourceMethodPayload {
  userId: string
  methodId: string
  preference: number
  proficiency: number
}

export type UpdateResourceMethodPayload = Partial<CreateResourceMethodPayload>

async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({ 'X-Tenant-Id': tenantId, ...init?.headers }),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    // The server's own sentence is the useful one here: it names the person who
    // is not a member, or the method the option set does not offer.
    let message = text
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text
    } catch {
      // keep the raw text
    }
    throw new Error(message || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const resourceCapabilitiesApi = {
  options: (tenantId: string) =>
    request<CapabilityOptions>('/resource-capabilities/options', tenantId),

  listProcedures: (tenantId: string) =>
    request<ResourceProcedureRow[]>('/resource-capabilities/procedures', tenantId),

  createProcedure: (tenantId: string, payload: CreateResourceProcedurePayload) =>
    request<ResourceProcedureRow>('/resource-capabilities/procedures', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateProcedure: (tenantId: string, id: string, payload: UpdateResourceProcedurePayload) =>
    request<ResourceProcedureRow>(`/resource-capabilities/procedures/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  removeProcedure: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/resource-capabilities/procedures/${id}`, tenantId, {
      method: 'DELETE',
    }),

  listMethods: (tenantId: string) =>
    request<ResourceMethodRow[]>('/resource-capabilities/methods', tenantId),

  createMethod: (tenantId: string, payload: CreateResourceMethodPayload) =>
    request<ResourceMethodRow>('/resource-capabilities/methods', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateMethod: (tenantId: string, id: string, payload: UpdateResourceMethodPayload) =>
    request<ResourceMethodRow>(`/resource-capabilities/methods/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  removeMethod: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/resource-capabilities/methods/${id}`, tenantId, { method: 'DELETE' }),

  // ─── The methods themselves ───────────────────────────────────────────────
  // `method-definitions`, not `methods`: that path is the QUALIFICATION grid —
  // who is qualified in what — and these are the collections being qualified in.

  listMethodDefinitions: (tenantId: string) =>
    request<MethodRow[]>('/resource-capabilities/method-definitions', tenantId),

  createMethodDefinition: (tenantId: string, payload: MethodDefinitionPayload) =>
    request<MethodRow>('/resource-capabilities/method-definitions', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /** `fieldIds` is a whole-list REPLACE; omit it to leave the steps alone. */
  updateMethodDefinition: (
    tenantId: string,
    id: string,
    payload: Partial<MethodDefinitionPayload>,
  ) =>
    request<MethodRow>(`/resource-capabilities/method-definitions/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  removeMethodDefinition: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/resource-capabilities/method-definitions/${id}`, tenantId, {
      method: 'DELETE',
    }),

  /**
   * A process step's methods — the SAME membership the method-definition calls
   * write, addressed from the step. What the Field Attributes editor uses.
   */
  listStepMethods: (tenantId: string, fieldId: string) =>
    request<MethodOption[]>(
      `/resource-capabilities/process-steps/${encodeURIComponent(fieldId)}/methods`,
      tenantId,
    ),

  /** Whole-list replace, scoped to this step. */
  setStepMethods: (tenantId: string, fieldId: string, methodIds: string[]) =>
    request<MethodOption[]>(
      `/resource-capabilities/process-steps/${encodeURIComponent(fieldId)}/methods`,
      tenantId,
      { method: 'PUT', body: JSON.stringify({ methodIds }) },
    ),

  /** Valid choices plus the current matrix, for the downloadable template. */
  importTemplate: (tenantId: string) =>
    request<QualificationTemplate>('/resource-capabilities/import/template', tenantId),

  /** Dry run: what each row would do, and every error. Writes nothing. */
  importPreview: (tenantId: string, payload: QualificationImportPayload) =>
    request<QualificationImportPlan>('/resource-capabilities/import/preview', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  /**
   * Apply a workbook. Merge only — nothing is ever deleted.
   *
   * The same payload as the preview, deliberately: the server recomputes the
   * plan rather than trusting one from the client, so a commit can never apply
   * something a preview did not show.
   */
  importCommit: (tenantId: string, payload: QualificationImportPayload) =>
    request<QualificationImportResult>('/resource-capabilities/import/commit', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}


// ─── Bulk assignment from a workbook ────────────────────────────────────────

/** A cell, in the shapes the import endpoints accept. */
export type QualificationCell = string | number | boolean | null

export interface QualificationSheetPayload {
  headers: string[]
  rows: { cells: QualificationCell[] }[]
}

export interface QualificationImportPayload {
  procedures?: QualificationSheetPayload
  methods?: QualificationSheetPayload
}

/** What a template workbook may name, plus the matrix stored today. */
export interface QualificationTemplate {
  sheets: { procedures: string; methods: string }
  columns: { procedures: string[]; methods: string[] }
  people: { email: string; name: string }[]
  methods: { name: string; stepCount: number }[]
  procedures: { name: string }[]
  /** Each row carries the target's ID as well as its name — a name is not unique. */
  assignments: {
    procedures: { person: string; procedure: string; procedureId: string }[]
    methods: {
      person: string
      method: string
      methodId: string
      preference: number
      proficiency: number
    }[]
  }
}

export type QualificationRowAction = 'create' | 'update' | 'noop'

export interface PlannedProcedureRow {
  sheet: 'procedures'
  rowNumber: number
  action: QualificationRowAction
  personLabel: string
  procedureLabel: string
}

export interface PlannedMethodRow {
  sheet: 'methods'
  rowNumber: number
  action: QualificationRowAction
  personLabel: string
  methodLabel: string
  preference?: number
  proficiency?: number
  /** Which stored values this row changes. Empty on a create. */
  changed: string[]
}

export interface QualificationRowNote {
  sheet: 'procedures' | 'methods'
  /** 1-based as the author sees it in Excel: the header is row 1. */
  rowNumber: number
  message: string
}

export interface QualificationImportPlan {
  procedures: PlannedProcedureRow[]
  methods: PlannedMethodRow[]
  errors: QualificationRowNote[]
  warnings: QualificationRowNote[]
  sheetErrors: string[]
  counts: Record<QualificationRowAction, number> & { errors: number }
}

export interface QualificationImportResult extends QualificationImportPlan {
  applied: { procedures: number; methods: number }
}

export interface MethodDefinitionPayload {
  name: string
  description?: string
  /** Process field ids, from `CapabilityOptions.processSteps`. */
  fieldIds?: string[]
}
